import { Component, OnInit, Input } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'app/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { PostService } from 'app/core/post.service';
import { PostBuilderComponent } from 'app/forum/components/post-builder/post-builder.component';
import { ForumPost } from 'app/forum/models/forum-post';
import { GroupType } from 'app/groups/models/group-type';
import { GroupService } from 'app/groups/services/group.service';
import { Observable, combineLatest } from 'rxjs';
import { take, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-scheduled-posts-dialog',
  template: `
    <h2>{{ 'ScheduledPosts' | preventHardcoding }}</h2>

    <div mat-dialog-content>
      {{ 'ScheduledPostsDescription' | preventHardcoding }}
    </div>

    <ng-container *ngFor="let g of groupedPlannedPostsWithNames$ | async">
      <div class="group-header">
        {{ g.groupName }}
      </div>

      <div class="post-item" *ngFor="let post of g.posts; trackBy: trackByFn">
        <mat-divider></mat-divider>

        <div class="post-row">
          <div class="post-date">
            {{ 'ScheduledFor' | preventHardcoding }}
            {{ post?.plannedAt | date : 'dd. MMM yyyy, HH:mm' }}
          </div>

          <button
            mat-icon-button
            class="post-actions"
            [matMenuTriggerFor]="postMenu"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </div>

        <mat-menu #postMenu="matMenu">
          <button mat-menu-item (click)="editPost(post)">
            <mat-icon>edit</mat-icon>
            {{ 'edit' | preventHardcoding }}
          </button>
          <button mat-menu-item (click)="deletePost(post)">
            <mat-icon>delete</mat-icon>
            {{ 'delete' | preventHardcoding }}
          </button>
        </mat-menu>

        <div class="post-title">
          {{ post?.title }}
        </div>
      </div>
    </ng-container>

    <div class="dialog-actions">
      <button
        mat-raised-button
        mat-dialog-close
        color="primary"
        matTooltipPosition="above"
        [matTooltip]="'Back' | preventHardcoding"
        (click)="cancel()"
      >
        {{ 'Back' | preventHardcoding }}
      </button>
    </div>
  `,
  styleUrls: ['./scheduled-posts-dialog.component.scss'],
})
export class ScheduledPostsDialogComponent implements OnInit {
  groupedPlannedPostsWithNames$: Observable<
    {
      groupId: string;
      groupType: GroupType;
      groupName: string;
      posts: ForumPost[];
    }[]
  >;

  constructor(
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ScheduledPostsDialogComponent>,
    private postService: PostService,
    private groupService: GroupService
  ) {}

  ngOnInit() {
    this.groupedPlannedPostsWithNames$ = this.postService
      .getAllUserPlannedPosts()
      .pipe(
        map((posts) => {
          const groups: { [id: string]: ForumPost[] } = {};
          for (const post of posts) {
            if (!groups[post.groupId]) groups[post.groupId] = [];
            groups[post.groupId].push(post);
          }
          return groups;
        }),
        switchMap((grouped) => {
          const entries = Object.entries(grouped);

          return combineLatest(
            entries.map(([groupId, posts]) =>
              this.groupService
                .getGroupName$(posts[0].groupId, posts[0].groupType)
                .pipe(
                  map((groupName) => ({
                    groupId,
                    groupType: posts[0].groupType,
                    groupName,
                    posts,
                  }))
                )
            )
          );
        })
      );
    this.dialogRef.keydownEvents().subscribe((event) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.cancel();
      }
    });
  }

  getGroupName$(key: string, groupType: GroupType): Observable<string> {
    return this.groupService.getGroupName$(key, groupType);
  }

  editPost(post: ForumPost) {
    const dialogRef = this.dialog.open(PostBuilderComponent, {
      width: 'calc(-24px + 100vw)',
      maxWidth: '840px',
      panelClass: 'create-post-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.post = post;
  }

  async deletePost(post: ForumPost) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {});
    dialogRef.componentInstance.title = 'deletePlannedPostTitle';
    dialogRef.componentInstance.text = 'deletePlannedPostBody';
    dialogRef.componentInstance.yesText = 'yes';
    dialogRef.componentInstance.noText = 'no';

    return new Promise((resolve) => {
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .toPromise()
        .then(async (yesDelete) => {
          if (yesDelete) {
            await this.postService.removePlannedPost(post);
          }
        });
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  trackByFn(index, item) {
    return item ? item.key : index;
  }
}
